import { ArrowUpDown } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

type SortableHeaderColumn = {
      toggleSorting: (desc?: boolean) => void;
      getIsSorted: () => false | "asc" | "desc";
};
const dataTableSortIconClassName = "ml-1 size-3 shrink-0 opacity-50";

export const dataTableHeaderButtonClassName =
      "h-10 px-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-brand-ink/40 hover:bg-transparent hover:text-brand-ink/60 md:h-7 md:px-2 lg:px-3";
export const dataTableHeaderLabelClassName = "text-[10px] font-bold uppercase tracking-[0.12em] text-brand-ink/40";
export const dataTableHeaderRowClassName = "border-b border-brand-ink/[0.06]";
export const dataTableBodyRowClassName =
      "border-b border-brand-ink/[0.06] last:border-0 transition-colors duration-150 hover:bg-brand-ink/[0.025]";
export const dataTableBodyCellClassName = "py-0";
export const dataTablePrimaryTextClassName = "px-1 py-1 text-sm leading-6 text-foreground/90";
export const dataTableTooltipUnderlineClassName = "cursor-help underline decoration-dashed underline-offset-4";

export function DataTableToolbar({ className, ...props }: React.ComponentProps<"div">) {
      return <div className={cn("flex flex-wrap items-center gap-3", className)} {...props} />;
}

export function DataTableSurface({ className, style, ...props }: React.ComponentProps<"div">) {
      return (
            <div
                  className={cn("overflow-hidden rounded-xl border border-brand-ink/12 shadow-sm", className)}
                  style={style}
                  {...props}
            />
      );
}

export function DataTableBandHeader({ className, style, ...props }: React.ComponentProps<typeof TableHeader>) {
      return (
            <TableHeader
                  className={cn("border-b border-brand-ink/[0.03] bg-brand-ink/[0.03] [&_tr]:border-b-0", className)}
                  style={style}
                  {...props}
            />
      );
}

export function DataTableHeaderRow({ className, ...props }: React.ComponentProps<typeof TableRow>) {
      return <TableRow className={cn(dataTableHeaderRowClassName, className)} {...props} />;
}

export function DataTableFooter({
      summary,
      onPrevious,
      onNext,
      canPrevious,
      canNext,
      className,
}: {
      summary: React.ReactNode;
      onPrevious: () => void;
      onNext: () => void;
      canPrevious: boolean;
      canNext: boolean;
      className?: string;
}) {
      return (
            <div className={cn("flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-end", className)}>
                  <div className="w-full text-sm text-muted-foreground sm:flex-1">{summary}</div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
                        <Button
                              variant="outline"
                              size="sm"
                              className="bg-card"
                              onClick={onPrevious}
                              disabled={!canPrevious}
                        >
                              Previous
                        </Button>
                        <Button variant="outline" size="sm" className="bg-card" onClick={onNext} disabled={!canNext}>
                              Next
                        </Button>
                  </div>
            </div>
      );
}

export function DataTableSortableHeader({
      column,
      align = "left",
      className,
      children,
}: {
      column: SortableHeaderColumn;
      align?: "left" | "center";
      className?: string;
      children: React.ReactNode;
}) {
      const button = (
            <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                  className={cn(dataTableHeaderButtonClassName, className)}
            >
                  {children}
                  <ArrowUpDown className={dataTableSortIconClassName} />
            </Button>
      );

      if (align === "center") {
            return <div className="text-center">{button}</div>;
      }

      return button;
}

export function DataTableStaticHeader({
      align = "left",
      className,
      children,
}: {
      align?: "left" | "center";
      className?: string;
      children: React.ReactNode;
}) {
      return (
            <div className={cn(align === "center" && "text-center", dataTableHeaderLabelClassName, className)}>
                  {children}
            </div>
      );
}
